import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Log } from 'ng2-logger';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StrainRegistryEntry } from "@app/strain/registry/models/strain-registry-entry.model";
import { SampleBoard } from "@app/strain/sample-boards/models/sample-board.model";

@Component({
    selector: 'app-strain-pcb-form',
    templateUrl: './strain-pcb-form.component.html',
    styleUrls: ['./strain-pcb-form.component.scss']
})
export class StrainPcbFormComponent implements OnInit, OnChanges {
    _logger = Log.create(StrainPcbFormComponent.name);

    @Input() showValidations = false;
    @Input() isEditMode: boolean;
    @Input() canAddItems = true;
    @Input() canDeleteItems = true;
    @Input() isLoading = false;
    @Input() sampleBoards: SampleBoard[] = [];

    public form: UntypedFormGroup;

    constructor(private fb: UntypedFormBuilder) {

    }

    ngOnInit() {
        this.form = this.fb.group({
            id: [null],
            requiredSampleBoards: this.fb.array([])
        });
        this._logger.info("ngOnInit:: Preparing strain configuration form", this.form);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.sampleBoards && this.form && (this.sampleBoards || []).length > 0) {
            this.arrayControl.controls.forEach(control => this.ensureSampleBoardSelection(control as UntypedFormGroup));
        }
    }

    sampleBoardControl(index) {
        return (this.arrayControl.controls[index] as UntypedFormGroup).get('sampleBoardId') as UntypedFormControl;
    }

    requiredQuantityControl(index) {
        return (this.arrayControl.controls[index] as UntypedFormGroup).get('requiredQuantity') as UntypedFormControl;
    }

    spareQuantityControl(index) {
        return (this.arrayControl.controls[index] as UntypedFormGroup).get('spareQuantity') as UntypedFormControl;
    }

    control(name: string): UntypedFormControl {
        return this.form.get(name) as UntypedFormControl;
    }

    get arrayControl() {
        return this.form.get('requiredSampleBoards') as UntypedFormArray;
    }

    value() {
        this._logger.info("value::", this.form);
        return this.form.value;
    }

    valid() {
        return this.form && this.form.valid;
    }


    reset() {
        this._logger.info("resetForm:: Resetting form", this.form);
        this.form.reset();

        while (this.arrayControl.length) {
            this.arrayControl.removeAt(0);
        }
    }

    addItem() {
        this.arrayControl.push(this.buildPCBItem());
        this.showValidations = false;
    }

    removeItem(index) {
        this.arrayControl.removeAt(index);
    }

    private buildPCBItem(item = null): UntypedFormGroup {
        const group = this.fb.group({
            sampleBoardId: [this.resolveInitialSampleBoard(item?.sampleBoard), Validators.required],
            requiredQuantity: [item?.requiredQuantity ?? 0, [Validators.required, Validators.min(0)]],
            spareQuantity: [item?.spareQuantity ?? 0, [Validators.required, Validators.min(0)]]
        });

        this.ensureSampleBoardSelection(group);
        return group;
    }

    load(entry: StrainRegistryEntry) {
        entry.sampleBoards.forEach(item => {
            this.arrayControl.push(this.buildPCBItem(item));
        })
    }

    private resolveInitialSampleBoard(sampleBoard?: SampleBoard): SampleBoard | number | null {
        if (sampleBoard?.id) {
            const match = (this.sampleBoards || []).find(opt => opt.id === sampleBoard.id);
            if (match) {
                return match;
            }
            return sampleBoard.id;
        }
        return (this.sampleBoards || [])[0] ?? null;
    }

    private ensureSampleBoardSelection(group: UntypedFormGroup): void {
        if (!group) {
            return;
        }

        const control = group.get('sampleBoardId');
        if (!control) {
            return;
        }

        const options = this.sampleBoards || [];
        if (options.length === 0) {
            return;
        }

        const currentId = this.getSampleBoardId(control.value);
        const nextSelection = options.find(opt => this.getSampleBoardId(opt) === currentId) || options[0];
        if (control.value !== nextSelection) {
            control.patchValue(nextSelection, { emitEvent: false });
        }
    }

    private getSampleBoardId(sampleBoard: SampleBoard | number): number | null {
        const raw = sampleBoard && typeof sampleBoard === 'object' ? sampleBoard.id : sampleBoard;
        return raw === null || raw === undefined ? null : Number(raw);
    }
}
